import React from 'react';
import { isWhitespace, isWordChar, Line } from './namedEntityRecognitionParser';
import _ from 'lodash';

// LineRanges are inclusive on endLineIdx but exclusive on endOffset
export interface LineRange {
  startLineIdx: number;
  endLineIdx: number;
  startOffset: number;
  endOffset: number;
}

function lineRangeContains(range: LineRange, lineIdx: number, offset: number) {
  if (lineIdx < range.startLineIdx || lineIdx > range.endLineIdx) {
    return false;
  }
  if (lineIdx === range.startLineIdx && offset < range.startOffset) {
    return false;
  }
  if (lineIdx === range.endLineIdx && offset >= range.endOffset) {
    return false;
  }
  return true;
}

function getCurrentLineSelectionRange(
  annotations: LineRange[],
  allowOverlappingAnnotations: boolean,
): LineRange | null {
  const windowSelection = window.getSelection();
  if (windowSelection && !windowSelection.isCollapsed) {
    const range = windowSelection.getRangeAt(0);

    const startLine = range.startContainer.parentElement;
    const endLine = range.endContainer.parentElement;
    if (!startLine || !endLine) {
      return null;
    }

    const startLineIdx = parseInt(startLine.dataset.tokenIdx!, 10);
    const startOffset = range.startOffset;
    const endLineIdx = parseInt(endLine.dataset.tokenIdx!, 10);
    const endOffset = range.endOffset;

    range.detach();

    if (_.isNaN(startLineIdx) || _.isNaN(endLineIdx)) {
      return null;
    }

    const lineRange = {
      startLineIdx,
      startOffset,
      endLineIdx,
      endOffset,
    };

    if (allowOverlappingAnnotations) {
      return lineRange;
    }

    // To determine if the selection is backwards, create a new range from the anchor node to the
    // focus node. If the anchor node is after the focus node, the range will collapse.
    const testRange = document.createRange();
    testRange.setStart(windowSelection.anchorNode!, windowSelection.anchorOffset);
    testRange.setEnd(windowSelection.focusNode!, windowSelection.focusOffset);
    const isBackwards = testRange.collapsed;
    testRange.detach();

    // Clip the selection if it overlaps with any annotations
    // TODO: this code is pretty imperformant
    if (!isBackwards) {
      const hasOverlappingAnnotation = !!_.find(annotations, annotation =>
        lineRangeContains(annotation, startLineIdx, startOffset),
      );
      if (hasOverlappingAnnotation) {
        return null;
      }
      const clippingAnnotation = _.first(
        _.sortBy(
          annotations.filter(({ startLineIdx, startOffset }) =>
            lineRangeContains(lineRange, startLineIdx, startOffset),
          ),
          ({ startLineIdx }) => startLineIdx,
          ({ startOffset }) => startOffset,
        ),
      );
      if (clippingAnnotation) {
        lineRange.endLineIdx = clippingAnnotation.startLineIdx;
        lineRange.endOffset = clippingAnnotation.startOffset;
      }
    } else {
      const hasOverlappingAnnotation = !!_.find(annotations, annotation =>
        lineRangeContains(annotation, endLineIdx, endOffset),
      );
      if (hasOverlappingAnnotation) {
        return null;
      }
      const clippingAnnotation = _.last(
        _.sortBy(
          annotations.filter(({ endLineIdx, endOffset }) =>
            lineRangeContains(lineRange, endLineIdx, endOffset),
          ),
          ({ endLineIdx }) => endLineIdx,
          ({ endOffset }) => endOffset,
        ),
      );
      if (clippingAnnotation) {
        lineRange.startLineIdx = clippingAnnotation.endLineIdx;
        lineRange.startOffset = clippingAnnotation.endOffset;
      }
    }

    return lineRange;
  }

  return null;
}

export function useLineSelectionRange(
  annotations: LineRange[],
  allowOverlappingAnnotations = false,
) {
  const [range, setRange] = React.useState<LineRange | null>(null);

  React.useEffect(() => {
    const handleSelectionChange = () => {
      setRange(getCurrentLineSelectionRange(annotations, allowOverlappingAnnotations));
    };
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => document.removeEventListener('selectionchange', handleSelectionChange);
  }, [annotations, allowOverlappingAnnotations]);

  return range;
}

function expandStartToken(
  selectionRange: LineRange,
  lines: Line[],
  existingAnnotations: LineRange[],
  allowOverlappingAnnotations: boolean,
): LineRange {
  const startLine = lines[selectionRange.startLineIdx].value;
  let minStartOffset: number;
  if (allowOverlappingAnnotations) {
    minStartOffset = 0;
  } else {
    const previousAnnotations = existingAnnotations.filter(
      ({ endLineIdx, endOffset }) =>
        endLineIdx === selectionRange.startLineIdx && endOffset <= selectionRange.startOffset,
    );
    minStartOffset = _.max(previousAnnotations.map(({ endOffset }) => endOffset)) ?? 0;
  }

  let newStartOffset = selectionRange.startOffset;
  if (isWordChar(startLine, newStartOffset)) {
    // The start of the selection range occurs within a word; extend until we reach the start of
    // the word or an existing annotation.
    while (isWordChar(startLine, newStartOffset - 1) && newStartOffset > minStartOffset) {
      newStartOffset--;
    }
  }

  return {
    ...selectionRange,
    startOffset: newStartOffset,
  };
}

function truncateStartWhitespace(selectionRange: LineRange, lines: Line[]): LineRange | null {
  let newStartLineIdx = selectionRange.startLineIdx;
  let newStartOffset = selectionRange.startOffset;

  // The start of the selection range potentially occurs within whitespace; truncate until we
  // either remove all the leading whitespace or have fully collapsed the selection.
  for (;;) {
    if (isWhitespace(lines[newStartLineIdx]?.value.charAt(newStartOffset))) {
      newStartOffset++;
    } else if (newStartOffset >= lines[newStartLineIdx]?.value.length) {
      // Reaching the end of the line doesn't mean we can stop truncating :)
      newStartLineIdx++;
      newStartOffset = 0;
    } else {
      break;
    }

    if (
      newStartLineIdx === selectionRange.endLineIdx &&
      newStartOffset === selectionRange.endOffset
    ) {
      return null;
    }
  }

  return {
    ...selectionRange,
    startLineIdx: newStartLineIdx,
    startOffset: newStartOffset,
  };
}

function expandEndToken(
  selectionRange: LineRange,
  lines: Line[],
  existingAnnotations: LineRange[],
  allowOverlappingAnnotations: boolean,
) {
  const endLine = lines[selectionRange.endLineIdx].value;
  let maxEndOffset: number;
  if (allowOverlappingAnnotations) {
    maxEndOffset = lines[selectionRange.endLineIdx].value.length;
  } else {
    const subsequentAnnotations = existingAnnotations.filter(
      ({ startLineIdx, startOffset }) =>
        startLineIdx === selectionRange.endLineIdx && startOffset >= selectionRange.endOffset,
    );
    maxEndOffset =
      _.min(subsequentAnnotations.map(({ startOffset }) => startOffset)) ??
      lines[selectionRange.endLineIdx].value.length;
  }

  let newEndOffset = selectionRange.endOffset;
  if (isWordChar(endLine, newEndOffset - 1)) {
    // The end of the selection range occurs within a word; extend until we reach the end of
    // the word or an existing annotation.
    while (isWordChar(endLine, newEndOffset) && newEndOffset < maxEndOffset) {
      newEndOffset++;
    }
  }

  return {
    ...selectionRange,
    endOffset: newEndOffset,
  };
}

function truncateEndWhitespace(selectionRange: LineRange, lines: Line[]) {
  let newEndLineIdx = selectionRange.endLineIdx;
  let newEndOffset = selectionRange.endOffset;

  // The end of the selection range potentially occurs within whitespace; truncate until we
  // either remove all the trailing whitespace or have fully collapsed the selection.
  for (;;) {
    if (isWhitespace(lines[newEndLineIdx].value.charAt(newEndOffset - 1))) {
      newEndOffset--;
    } else if (newEndOffset === 0) {
      newEndLineIdx--;
      newEndOffset = lines[newEndLineIdx].value.length;
    } else {
      break;
    }
    if (
      newEndLineIdx === selectionRange.startLineIdx &&
      newEndOffset === selectionRange.startOffset
    ) {
      return null;
    }
  }

  return {
    ...selectionRange,
    endLineIdx: newEndLineIdx,
    endOffset: newEndOffset,
  };
}

export function getAnnotationRangeFromSelectionRange(
  selectionRange: LineRange,
  lines: Line[],
  existingAnnotations: LineRange[],
  allowSubstringSelection = false,
  allowOverlappingAnnotations = false,
  // preserveTrailingPunctuation = false,
): LineRange | null {
  // Note that these functions make the assumption that the provided range does not overlap
  // any existing annotations if allowOverlappingAnnotations is false.
  let adjustedRange: LineRange | null = selectionRange;
  if (!allowSubstringSelection) {
    adjustedRange = expandStartToken(
      adjustedRange,
      lines,
      existingAnnotations,
      allowOverlappingAnnotations,
    );
  }
  adjustedRange = truncateStartWhitespace(adjustedRange, lines);
  if (!adjustedRange) {
    return null;
  }

  if (!allowSubstringSelection) {
    adjustedRange = expandEndToken(
      adjustedRange,
      lines,
      existingAnnotations,
      allowOverlappingAnnotations,
    );
  }
  adjustedRange = truncateEndWhitespace(adjustedRange, lines);
  return adjustedRange;
}
